import {
  clearBuildingAssets,
  clearFloorAsset,
  clearSiteAssets,
  setBuildingAsset,
  setFloorAsset,
  setSiteAsset,
} from '@/lib/api/spaces.js'

export const MODEL_ASSET_TYPES = ['fbx', 'glb', 'usdz']
export const MODEL_FILE_EXTENSIONS = ['.fbx', '.glb', '.gltf', '.usdz', '.usd', '.usdc']

export function isModelAssetType(assetType) {
  return MODEL_ASSET_TYPES.includes(String(assetType || '').toLowerCase())
}

export function inferModelAssetType(name) {
  const lower = String(name || '').toLowerCase()
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'glb'
  if (lower.endsWith('.usdz') || lower.endsWith('.usd') || lower.endsWith('.usdc')) return 'usdz'
  return 'fbx'
}

const bundledModelModules = import.meta.glob('/public/models/*.{fbx,glb,gltf,usdz,usd,usdc}', {
  eager: true,
  query: '?url',
  import: 'default',
})

function labelFromFileName(fileName) {
  return fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const BUNDLED_MODEL_PRESETS = Object.entries(bundledModelModules)
  .map(([filePath, url]) => {
    const fileName = filePath.split('/').pop()
    return {
      id: `asset:${fileName}`,
      label: `Asset: ${labelFromFileName(fileName)}`,
      payload: {
        local_url: url,
        file_name: fileName,
        asset_type: inferModelAssetType(fileName),
      },
    }
  })
  .sort((a, b) => a.label.localeCompare(b.label))

/**
 * Preset 3D models. Public model files under /public/models are added automatically.
 * Buildings should be the primary target; sites are optional exterior context.
 */
export const MODEL_PRESETS = [
  { id: 'none', label: 'No 3D model', payload: null },
  ...BUNDLED_MODEL_PRESETS,
  {
    id: 'newport',
    label: 'Demo: Newport Airbnb',
    payload: {
      local_url: '/fixtures/newport-airbnb.fbx',
      file_name: 'newport-airbnb.fbx',
      asset_type: 'fbx',
      transform: { scale: 0.01 },
    },
  },
  { id: 'custom', label: 'Custom URL or path…', payload: null },
]

export function pickModelAsset(entity) {
  return (entity?.assets || []).find((a) => isModelAssetType(a.asset_type)) || null
}

export function initialModelForm(asset) {
  const empty = {
    preset: 'none',
    local_url: '',
    file_name: '',
    asset_type: 'fbx',
    bucket: '',
    storage_key: '',
    scale: '',
    north_rotation: '',
    origin_x: '',
    origin_y: '',
    elevation: '',
  }
  const hasContent = asset?.local_url || (asset?.bucket && asset?.bucket !== 'local' && asset?.storage_key)
  if (!hasContent) return empty
  const preset = MODEL_PRESETS.find(
    (p) =>
      p.payload &&
      p.payload.local_url === asset.local_url &&
      (p.payload.asset_type || 'fbx') === (asset.asset_type || 'fbx'),
  )
  const t = asset.transform || {}
  const remoteBucket = asset.bucket && asset.bucket !== 'local' ? asset.bucket : ''
  const remoteKey = remoteBucket ? asset.storage_key || '' : ''
  if (preset) {
    return {
      preset: preset.id,
      local_url: asset.local_url || '',
      file_name: asset.file_name || '',
      asset_type: asset.asset_type || 'fbx',
      bucket: remoteBucket,
      storage_key: remoteKey,
      scale: t.scale !== undefined && t.scale !== 1 ? String(t.scale) : '',
      north_rotation: t.north_rotation ? String(t.north_rotation) : '',
      origin_x: t.origin_x ? String(t.origin_x) : '',
      origin_y: t.origin_y ? String(t.origin_y) : '',
      elevation: t.elevation ? String(t.elevation) : '',
    }
  }
  return {
    preset: remoteBucket ? 'library' : 'custom',
    local_url: asset.local_url || '',
    file_name: asset.file_name || '',
    asset_type: asset.asset_type || 'fbx',
    bucket: remoteBucket,
    storage_key: remoteKey,
    scale: t.scale !== undefined ? String(t.scale) : '',
    north_rotation: t.north_rotation !== undefined ? String(t.north_rotation) : '',
    origin_x: t.origin_x !== undefined ? String(t.origin_x) : '',
    origin_y: t.origin_y !== undefined ? String(t.origin_y) : '',
    elevation: t.elevation !== undefined ? String(t.elevation) : '',
  }
}

export function buildModelSetPayload(form) {
  const url = String(form.local_url || '').trim()
  const bucket = String(form.bucket || '').trim()
  const storageKey = String(form.storage_key || '').trim()
  const hasRemote = bucket && bucket !== 'local' && storageKey
  if (!url && !hasRemote) return null
  const payload = {
    local_url: url || undefined,
    file_name: String(form.file_name || '').trim() || undefined,
    asset_type: form.asset_type || 'fbx',
    transform: {},
  }
  if (hasRemote) {
    payload.bucket = bucket
    payload.storage_key = storageKey
  }
  for (const key of ['scale', 'origin_x', 'origin_y', 'elevation', 'north_rotation']) {
    const raw = form[key]
    if (raw === '' || raw === null || raw === undefined) continue
    const num = Number(raw)
    if (Number.isFinite(num)) payload.transform[key] = num
  }
  return payload
}

export function modelPersistencePlan(initialAsset, form) {
  const had = Boolean(initialAsset?.local_url)
  const wantsNone = form.preset === 'none'
  if (wantsNone) return had ? { action: 'clear' } : { action: 'noop' }
  const payload = buildModelSetPayload(form)
  if (!payload) return { action: 'invalid' }
  return { action: 'set', payload }
}

const ADAPTERS = {
  site: { clearAll: clearSiteAssets, setOne: setSiteAsset },
  building: { clearAll: clearBuildingAssets, setOne: setBuildingAsset },
  floor: { clearAll: (floorId) => clearFloorAsset(floorId), setOne: setFloorAsset },
}

export async function persistEntityModel(scope, entityId, previousEntity, form) {
  const adapter = ADAPTERS[scope]
  if (!adapter) throw new Error(`Unknown model scope: ${scope}`)
  const initial = pickModelAsset(previousEntity)
  const plan = modelPersistencePlan(initial, form)
  if (plan.action === 'invalid') {
    const err = new Error('Enter a URL or path for the 3D model, or pick "No 3D model".')
    err.uiMessage = err.message
    throw err
  }
  if (plan.action === 'noop') return
  if (plan.action === 'clear') {
    await adapter.clearAll(entityId)
    return
  }
  await adapter.clearAll(entityId)
  await adapter.setOne(entityId, plan.payload)
}
